/**
 * Which region each organization this browser has seen lives in.
 *
 * The Auth0 tenants claim is the cross-region index, but it lags: it is only
 * rewritten on create, invite-accept and role change, and only for the user it
 * concerns. A hint recorded here keeps two things working while a claim is
 * stale — addressing a tenant's own regional API instead of the default one, and
 * knowing which regions are worth asking at all, so the registry does not have
 * to sweep every deployed region and create a user record in each (ESCLOUD-268).
 *
 * Hints are a routing convenience only. They are overwritten by whatever the
 * server says, and they never influence what the user is allowed to do — the API
 * resolves membership and role from its database on every request.
 *
 * The store is observable: the registry learns a tenant's authoritative region
 * only after its query returns, and whoever is already rendering that tenant has
 * to retarget its requests without waiting for a reload.
 */

const STORAGE_KEY = 'escloud-tenant-regions';

type Hints = Record<string, string>;

const listeners = new Set<() => void>();

function notify(): void {
    listeners.forEach((listener) => listener());
}

/** Subscribe to hint changes, including writes made by another tab. */
export function subscribeTenantRegions(listener: () => void): () => void {
    listeners.add(listener);

    const onStorage = (event: StorageEvent) => {
        if (event.key === STORAGE_KEY || event.key === null) listener();
    };
    window.addEventListener('storage', onStorage);

    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', onStorage);
    };
}

/**
 * Raw stored value, as the snapshot for `useSyncExternalStore`. Strings compare
 * by value, so an unchanged store does not re-render.
 */
export function readTenantRegionsSnapshot(): string {
    if (typeof window === 'undefined') return '';
    try {
        return localStorage.getItem(STORAGE_KEY) ?? '';
    } catch {
        return '';
    }
}

function parseHints(raw: string): Hints {
    if (!raw) return {};

    try {
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

        return Object.fromEntries(
            Object.entries(parsed as Record<string, unknown>)
                .filter(([, region]) => typeof region === 'string')
                .map(([tenantId, region]) => [tenantId, (region as string).trim().toLowerCase()])
                .filter(([, region]) => Boolean(region))
        );
    } catch {
        // Corrupted store — routing falls back to the claims and the default region.
        return {};
    }
}

function readHints(): Hints {
    return parseHints(readTenantRegionsSnapshot());
}

/** Region of one tenant, from an already-read snapshot (no storage access). */
export function readTenantRegionFrom(snapshot: string, tenantId: string): string | null {
    return parseHints(snapshot)[tenantId] ?? null;
}

export function readTenantRegion(tenantId: string): string | null {
    return readHints()[tenantId] ?? null;
}

export function readHintedRegions(): string[] {
    return [...new Set(Object.values(readHints()))];
}

export function rememberTenantRegion(tenantId: string, region: string): void {
    if (typeof window === 'undefined') return;

    const code = region.trim().toLowerCase();
    if (!tenantId || !code) return;

    const hints = readHints();
    if (hints[tenantId] === code) return;

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...hints, [tenantId]: code }));
    } catch {
        // Storage full, or blocked by policy (Safari private mode). A hint is a
        // convenience: losing it costs an extra region query, so it must never
        // propagate — least of all out of a mutation that already succeeded.
        return;
    }

    notify();
}

/**
 * Forget everything tenant-scoped this browser remembers. Called on sign-out:
 * on a shared clinical workstation the next user's registry must not inherit
 * these regions, which would query — and JIT-provision them in — a region they
 * have nothing in.
 */
export function forgetTenantRegions(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        return;
    }

    notify();
}
